import React from 'react';
import { X, Building2, MapPin, DollarSign, Clock, Calendar, CheckCircle, Globe, Award, ExternalLink, Briefcase, Mail, MailCheck } from 'lucide-react';
import { Job } from '../../types/jobs';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { openWebRedirect } from '../../lib/api';
import * as amplitude from '@amplitude/analytics-browser';
import { fetchJobById, fetchJobsOffers } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';

interface JobDetailModalProps {
  job: Job | null;
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  error: string | null;
}

export function JobDetailModal({ job, isOpen, onClose, isLoading, error }: JobDetailModalProps) {
  const { userInfo } = useAuth();
  const modalRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [touchEnd, setTouchEnd] = React.useState<number | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [relatedJobs, setRelatedJobs] = React.useState<Job[]>([]);
  const [isClosing, setIsClosing] = React.useState(false);
  const [currentJob, setCurrentJob] = React.useState<Job | null>(null);
  const [isLoadingRelated, setIsLoadingRelated] = React.useState(false);
  const [relatedError, setRelatedError] = React.useState<string | null>(null);

  // Add handleApply function
  const handleApply = () => {
    if (currentJob?.applyUrl) {
      // Track the apply click event in analytics
      amplitude.track('Job Apply Click', {
        jobId: currentJob.id,
        jobTitle: currentJob.title
      });
      // Use openWebRedirect to open the URL
      openWebRedirect(currentJob.applyUrl, userInfo);
    }
  };

  // Update currentJob when job prop changes
  React.useEffect(() => {
    setCurrentJob(job);
  }, [job]);

  // Format publication date with improved error handling
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Non spécifiée';
    
    try {
      // First check if the string is a valid date format
      const parsedDate = parseISO(dateString);
      if (!isValid(parsedDate)) {
        return 'Non spécifiée';
      }
      return format(parsedDate, 'd MMMM yyyy', { locale: fr });
    } catch (e) {
      console.error('Date formatting error:', e);
      return 'Non spécifiée';
    }
  };

  // Fetch related jobs when job changes
  React.useEffect(() => {
    const fetchRelatedJobs = async () => {
      if (!currentJob?.category) return;
      
      try {
        const data = await fetchJobsOffers(`/jobs?limit=3&category=${currentJob.category}`, userInfo);
        let jobsData: Job[] = [];
        
        if (Array.isArray(data)) {
          jobsData = data;
        } else if (data.data && Array.isArray(data.data)) {
          jobsData = data.data;
        }
        
        // Filter out the current job
        const filtered = jobsData.filter(j => j.id !== currentJob.id).slice(0, 3);
        setRelatedJobs(filtered);
      } catch (error) {
        console.error('Error fetching related jobs:', error);
      }
    };
    
    if (currentJob) {
      fetchRelatedJobs();
    }
  }, [currentJob]);

  // Close on escape key
  React.useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Lock body scroll
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      // Restore scroll
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle click outside of modal
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Smooth closing animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleRelatedJobClick = async (relatedJob: Job) => {
    try {
      // Track analytics
      amplitude.track('Related Job Click', {
        fromJobId: currentJob?.id,
        toJobId: relatedJob.id
      });

      // Scroll to top of modal content
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }

      // If we have full details already, just set the job
      if (relatedJob.description) {
        setCurrentJob(relatedJob);
        return;
      }

      // Otherwise, fetch the job details
      setIsLoadingRelated(true);
      setRelatedError(null);

      const jobDetail = await fetchJobById(relatedJob.id.toString(), userInfo);
      setCurrentJob(jobDetail);
    } catch (error) {
      console.error('Error loading related job details:', error);
      setRelatedError('Impossible de charger les détails de cette offre');
    } finally {
      setIsLoadingRelated(false);
    }
  };

  // Function to safely decode HTML and convert BBCode
  const decodeHTML = (html?: string) => {
    if (!html) return '';
    try {
      // First decode HTML entities
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      let content = txt.value;

      // Then convert BBCode to HTML
      // Replace [COLOR=value]text[/COLOR]
      content = content.replace(/\[COLOR=([^\]]+)\]([\s\S]*?)\[\/COLOR\]/g, (match, color, text) => {
        // Handle variables in color values like var(--tw-prose-bold)
        if (color.includes('--tw-prose-bold')) {
          return `<strong>${text}</strong>`;
        }
        // Other color values
        return `<span style="color: ${color};">${text}</span>`;
      });

      // Replace [B] or [BOLD]
      content = content.replace(/\[B\]([\s\S]*?)\[\/B\]/gi, '<strong>$1</strong>');
      content = content.replace(/\[BOLD\]([\s\S]*?)\[\/BOLD\]/gi, '<strong>$1</strong>');

      // Replace [I] or [ITALIC]
      content = content.replace(/\[I\]([\s\S]*?)\[\/I\]/gi, '<em>$1</em>');
      content = content.replace(/\[ITALIC\]([\s\S]*?)\[\/ITALIC\]/gi, '<em>$1</em>');

      // Replace [U] or [UNDERLINE]
      content = content.replace(/\[U\]([\s\S]*?)\[\/U\]/gi, '<u>$1</u>');
      content = content.replace(/\[UNDERLINE\]([\s\S]*?)\[\/UNDERLINE\]/gi, '<u>$1</u>');
      
      // Replace [SIZE=value]text[/SIZE]
      content = content.replace(/\[SIZE=([^\]]+)\]([\s\S]*?)\[\/SIZE\]/gi, (match, size, text) => {
        // Convert size to pixels or relative unit
        return `<span style="font-size: ${size};">${text}</span>`;
      });
      
      // Replace [URL=url]text[/URL]
      content = content.replace(/\[URL=([^\]]+)\]([\s\S]*?)\[\/URL\]/gi, '<a href="$1" target="_blank" rel="noopener noreferrer">$2</a>');
      
      // Replace [URL]url[/URL]
      content = content.replace(/\[URL\]([\s\S]*?)\[\/URL\]/gi, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');

      return content;
    } catch (e) {
      console.error('HTML decode error:', e);
      return html || '';
    }
  };

  // Get employer display name
  const getEmployerDisplay = (employer: any): string => {
    if (typeof employer === 'object' && employer !== null && !Array.isArray(employer)) {
      // Handle the case where employer is an object with a name property
      return employer.name || "Employeur non spécifié";
    } else if (Array.isArray(employer) && employer.length > 0) {
      // Handle the case where employer is an array with content
      if (typeof employer[0] === 'string') {
        return employer[0];
      } else if (typeof employer[0] === 'object' && employer[0]?.name) {
        return employer[0].name;
      }
    } else if (typeof employer === 'string' && employer) {
      // Handle the case where employer is a string
      return employer;
    }
    
    // Fallback for empty arrays or null/undefined employers
    return "Employeur non spécifié";
  };

  // Format salary display
  const formatSalary = (job: Job): string => {
    if (job.salaryFull) return job.salaryFull;
    
    if (job.salary) {
      let display = `${job.salary}`;
      if (job.salaryCurrency) display += ` ${job.salaryCurrency}`;
      if (job.salaryPeriod) {
        // Convert period to more readable format
        const periodMap: {[key: string]: string} = {
          'hourly': 'par heure',
          'weekly': 'par semaine',
          'monthly': 'par mois',
          'yearly': 'par an',
        };
        display += ` ${periodMap[job.salaryPeriod] || job.salaryPeriod}`;
      }
      return display;
    }
    
    return "Non spécifié";
  };

  // If modal is closed, don't render anything
  if (!isOpen) return null;

  // Determine if we're showing the main job or a related job that's loading
  const showLoading = isLoading || isLoadingRelated;
  const showError = !showLoading && (error || relatedError);
  const jobToShow = currentJob;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-6 transition-all duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        ref={modalRef}
        className={`relative w-full h-full sm:h-auto sm:max-h-[90vh] bg-white dark:bg-gray-800 sm:rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 flex flex-col ${
          isClosing ? 'scale-95' : 'scale-100'
        }`}
        style={{maxWidth: '1024px'}}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-50 p-2 bg-white/90 dark:bg-gray-800/90 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors shadow-md"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </button>
        
        {/* Content - Scrollable */}
        <div 
          ref={contentRef}
          className="flex-1 overflow-y-auto custom-scrollbar"
        >
          {showLoading ? (
            <div className="flex items-center justify-center h-64 sm:h-[50vh]">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
            </div>
          ) : showError ? (
            <div className="p-6 sm:p-10 text-center">
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 sm:p-8">
                <h2 className="text-xl font-semibold text-red-900 dark:text-red-300 mb-4">
                  {error || relatedError}
                </h2>
                <p className="text-red-600 dark:text-red-400 mb-6">
                  Cette offre n'est peut-être plus disponible ou a été supprimée.
                </p>
                <button
                  onClick={handleClose}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                >
                  <span>Retourner aux offres d'emploi</span>
                </button>
              </div>
            </div>
          ) : jobToShow ? (
            <div>
              {/* Premium/Featured banner */}
              {(jobToShow.featured || jobToShow.employerVerified) && (
                <div className={`w-full py-2 sm:py-3 px-4 text-center text-white ${
                  jobToShow.featured 
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600' 
                    : 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                }`}>
                  <div className="flex items-center justify-center gap-2">
                    {jobToShow.featured ? (
                      <>
                        <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-sm sm:text-base font-medium">Offre Premium</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-sm sm:text-base font-medium">Employeur vérifié</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Job header */}
              <div className="p-6 sm:p-10 border-b border-gray-200 dark:border-gray-700/50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                      {decodeHTML(jobToShow.title)}
                    </h1>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500" />
                      <span className="text-base sm:text-lg font-medium text-gray-700 dark:text-gray-300">
                        {getEmployerDisplay(jobToShow.employer)}
                      </span>
                    </div>
                  </div>
                  
                  {jobToShow.applyUrl && (
                    <button
                      onClick={handleApply}
                      className={`flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 text-white font-medium rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-[1.03] ${
                        jobToShow.featured 
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' 
                          : 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700'
                      }`}
                    >
                      <span className="text-sm sm:text-base">Postuler</span>
                      <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Key details */}
              <div className="px-6 sm:px-10 py-6 sm:py-8 border-b dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100/70 dark:border-gray-700/30">
                    <div className="flex items-start gap-4">
                      <div className="p-2 sm:p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow-sm">
                        <MapPin className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Lieu</div>
                        <div className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mt-1">
                          {jobToShow.location || 'Non spécifié'}
                          {jobToShow.locationFlexible === 1 && (
                            <span className="ml-2 text-xs sm:text-sm text-blue-500 dark:text-blue-400 font-normal inline-flex items-center gap-1.5">
                              <Globe className="w-3.5 h-3.5" />
                              <span>Flexible</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100/70 dark:border-gray-700/30">
                    <div className="flex items-start gap-4">
                      <div className="p-2 sm:p-2.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg shadow-sm">
                        <DollarSign className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Salaire</div>
                        <div className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mt-1">
                          {formatSalary(jobToShow)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100/70 dark:border-gray-700/30">
                    <div className="flex items-start gap-4">
                      <div className="p-2 sm:p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg shadow-sm">
                        <Clock className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Date de début</div>
                        <div className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mt-1">
                          {jobToShow.startASAP ? 'Dès que possible' : 
                           (jobToShow.startDate ? formatDate(jobToShow.startDate) : 'Non spécifiée')}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100/70 dark:border-gray-700/30">
                    <div className="flex items-start gap-4">
                      <div className="p-2 sm:p-2.5 bg-purple-50 dark:bg-purple-900/20 rounded-lg shadow-sm">
                        <Calendar className="w-5 h-5 text-purple-500 dark:text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Publié le</div>
                        <div className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mt-1">
                          {formatDate(jobToShow.published)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Job description */}
              <div className="p-6 sm:p-10">
                <div className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-3">
                    <div className="p-1.5 sm:p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                      <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500 dark:text-primary-400" />
                    </div>
                    <span>Description du poste</span>
                  </h2>
                  <div 
                    className="prose prose-lg dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 
                              prose-headings:font-semibold 
                              prose-p:text-gray-700 dark:prose-p:text-gray-300
                              prose-strong:text-gray-900 dark:prose-strong:text-white
                              prose-li:text-gray-700 dark:prose-li:text-gray-300
                              prose-a:text-primary-600 dark:prose-a:text-primary-400"
                    dangerouslySetInnerHTML={{ 
                      __html: decodeHTML(jobToShow.description) || 'Aucune description disponible' 
                    }}
                  />
                </div>

                {/* Apply button - bottom */}
                {jobToShow.applyUrl && (
                  <div className="mt-8 pt-6 border-t dark:border-gray-700/50 flex flex-col sm:flex-row items-center gap-4 justify-between">
                    <div className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                      <p>Cette offre vous intéresse ? Postulez directement ci-dessous.</p>
                    </div>
                    <button
                      onClick={() => {
                        // Still track the click event
                        amplitude.track('Job Apply From Detail', {
                          jobId: jobToShow.id,
                          jobTitle: jobToShow.title || 'Unknown job',
                          featured: jobToShow.featured || false
                        });
                        // Use openWebRedirect instead of direct window.open
                        openWebRedirect(jobToShow.applyUrl, userInfo);
                      }}
                      className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 text-white font-medium rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                        jobToShow.featured 
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' 
                          : 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700'
                      }`}
                    >
                      <span>Postuler maintenant</span>
                      <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                )}

                {/* Job alerts CTA */}
                <div className="mt-8 sm:mt-12">
                  <div className="bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 rounded-xl p-4 sm:p-6 border border-primary-100/50 dark:border-primary-800/30">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="p-2.5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-sm flex-shrink-0">
                        <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500" />
                      </div>
                      <div className="flex-1 space-y-1">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                          Ne manquez aucune opportunité !
                        </h3>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                          Recevez les nouvelles offres d'emploi directement dans votre boîte mail
                        </p>
                      </div>
                      <div className="w-full sm:w-auto flex-shrink-0 mt-3 sm:mt-0">
                        <a 
                          href="https://pvtistes.net/jobs/index.php?do=notifications" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-xl transition-colors shadow-sm hover:shadow-md cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            amplitude.track("Job Alert Signup Click From Modal");
                            openWebRedirect("https://pvtistes.net/jobs/index.php?do=notifications", userInfo);
                          }}
                        >
                          <MailCheck className="w-4 h-4" />
                          <span>S'inscrire aux alertes</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Related Jobs Section */}
              {relatedJobs.length > 0 && (
                <div className="px-6 sm:px-10 pb-8 sm:pb-10">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
                    Offres similaires
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {relatedJobs.map(relatedJob => (
                      <div 
                        key={relatedJob.id}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md border border-gray-200/50 dark:border-gray-700/50 p-4 cursor-pointer transition-all duration-200 hover:-translate-y-1"
                        onClick={() => handleRelatedJobClick(relatedJob)}
                      >
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                          {decodeHTML(relatedJob.title)}
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 mb-2">
                          <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{getEmployerDisplay(relatedJob.employer)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{relatedJob.location || 'Lieu non spécifié'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 sm:p-10 text-center">
              <p className="text-gray-600 dark:text-gray-400">
                Aucune information sur cette offre d'emploi n'a été trouvée.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}